import 'lazysizes';
import { createApp } from 'vue';
import { createStore } from 'vuex';

import { hoistJsonScripts } from '@Foundation/utilities';

import './mctbCommerceConnector';

// Plugins for zone-level customizations
import loadZoneCustomizations from '@refapp/zone-plugin-loader';
import init from '@refapp/sisyphus';

// Sitecore-Vue bridge
import SitecoreVueAdapter from '@Foundation/sitecore/SitecoreVueAdapter';

// Helpers
import { requireAll } from '@Foundation/utilities/helpers';

// Foundation
import Foundation from '@Foundation';
import LoadScriptModule from '@Foundation/loadscript';
import eventBus, { exposeGlobals } from '@refapp/eventbus-js';
import { tag } from '@Foundation/analyticsHandler/directives/tag';

// Feature
import AlertModule from '@Feature/Alert';
import ArticleShareItem from '@Feature/PageContentExt';
import BazaarVoiceModule from '@Feature/Bazaarvoice';
import CarouselModule from '@Feature/Carousel';
import ColorDropdown from '@Feature/ColorDropdown';
import ColorSelector from '@Feature/ColorSelector';
import CommerceConnector from '@Feature/CommerceConnector';
import CookieBanner from '@Feature/PageContentExt';
import FilterModule from '@Feature/Filter';
import GenericForm from '@Feature/GenericForm';
import HaircareDiag from '@Feature/HaircareDiag';
import HaircolorDiag from '@Feature/HaircolorDiag';
import HaircolorTreat from '@Feature/HaircolorTreat';
import HeaderLogo from '@Feature/HeaderLogo';
import Iframepopup from '@Feature/IframePopup';
import InstagramComponent from '@Feature/Instagram';
import likebutton from '@Feature/LikeButton';
import LiveChat from '@Feature/LiveChat';
import ModalModule from '@Feature/Modal';
import NavigationModule from '@Feature/Navigation';
import NewsletterModule from '@Feature/NewsLetter';
import NotificationModule from '@Feature/Notification';
import OapAccountNavigation from '@Feature/OapAccountNavigation';
import OapBadge from '@Feature/OapBadge';
import OapBeautyGeniusCard from '@Feature/OapBeautyGeniusCard';
import OapBeautyGeniusWrapper from '@Feature/OapBeautyGeniusWrapper';
import OapBeautyProfile from '@Feature/OapBeautyProfile';
import OapCard from '@Feature/OapCard';
import OapCardPopin from '@Feature/OapCardPopin';
import OapCarouselControls from '@Feature/CarouselControls';
import OapChannelAdvisor from '@Feature/OapChannelAdvisor';
import OapConcerns from '@Feature/OapConcerns';
import OapContrastSwitcher from '@Feature/OapContrastSwitcher';
import OapCountdown from '@Feature/OapCountdown';
import OapDsfAppVtoWrapper from '@Feature/OapDsfAppVtoWrapper';
import OapDsfAppWrapper from '@Feature/OapDsfAppWrapper';
import OapEventfulTextInput from '@Feature/OapEventfulTextInput';
import OapEventsNavigation from '@Feature/OapEventsNavigation';
import OapFavorite from '@Feature/OapFavorite';
import OapFloatingIsland from '@Feature/OapFloatingIsland';
import OapFlowplayer from '@Feature/OapFlowplayer';
import OapFlowplayerPopin from '@Feature/OapFlowplayerPopin';
import OapFocusTrap from '@Feature/OapFocusTrap';
import OapGeolocationPopin from '@Feature/OapGeolocationPopin';
import OapIframe from '@Feature/OapIframe';
import OapBeforeAfter from '@Feature/OapBeforeAfter';
import OapImmersiveBeforeAfter from '@Feature/OapImmersiveBeforeAfter';
import OapImmersiveBeforeAfterVideo from '@Feature/OapImmersiveBeforeAfterVideo';
import OapImmersiveCopyOnVideo from '@Feature/OapImmersiveCopyOnVideo';
import OapImmersiveHotspots from '@Feature/OapImmersiveHotspots';
import OapImmersiveParallax from '@Feature/OapImmersiveParallax';
import OapImmersiveParallaxHero from '@Feature/OapImmersiveParallaxHero';
import OapLoader from '@Feature/OapLoader';
import OapMarquee from '@Feature/OapMarquee';
import OapMakeTheSwitch from '@Feature/MakeTheSwitch';
import OapMarkupRenderingDelay from '@Feature/OapMarkupRenderingDelay';
import OapMediaCarousel from '@Feature/OapMediaCarousel';
import OapMediaCover from '@Feature/OapMediaCover';
import OapMenShadeMatcher from '@Feature/MenShadeMatcher';
import OapPopin from '@Feature/OapPopin';
import OapPopup from '@Feature/OapPopup';
import OapProductIngredients from '@Feature/OapProductIngredients';
import OapProductVariantSelector from '@Feature/ProductVariantSelector';
import OapPageNavigation from '@Feature/OapPageNavigation';
import OapPromoBar from '@Feature/OapPromoBar';
import OapQuiz from '@Feature/Questionnaire';
import OapRating from '@Feature/OapRating';
import OapRoutineBuilder from '@Feature/RoutineBuilder';
import OapScroller from '@Feature/OapScroller';
import OapSkindrAppWrapper from '@Feature/OapSkindrAppWrapper';
import OapSlider from '@Feature/OapSlider';
import OapStories from '@Feature/OapStories';
import OapStrikethroughScrolling from '@Feature/OapStrikethroughScrolling';
import OapTabComponent from '@Feature/OapTab';
import OapToastNotification from '@Feature/OapToastNotification';
import OapUsage from '@Feature/OapUsage';
import OapVideoImmersive from '@Feature/OapVideoImmersive';
import OneTrustCookieHelper from '@Feature/OneTrust';
import Overlay from '@Feature/Overlay';
import productModule from '@Feature/ProductModule';
import RealDialog from '@Feature/RealDialog';
import RevTrax from '@Feature/RevTrax';
import Search from '@Feature/Search';
import SearchSuggestions from '@Feature/SearchSuggestions';
import Sfmc from '@Feature/SFMC';
import Slider from '@Feature/Slider';
import VideoPlaceholder from '@Feature/VideoPlaceholder';
import VirtualTryOn from '@Feature/VirtualTryOn';
import VueModels from '@Feature/VueModels';
import ERetailer from '@Feature/ERetailer';
import OapShadeFinderCarousel from '@Feature/OapShadeFinderCarousel';
import OapTechDetails from '@Feature/OapTechDetails';
import AccessibilityStore from '@Foundation/accessibilityStore';
import OapSustainability from '@Feature/OapSustainability';
import OapPromoCard from '@Feature/OapPromoCard';
import FooterNewsletterSignupModal from '@Feature/FooterNewsletterSignupModal';
import FloatingSidebar from '@Feature/FloatingSidebar';
import OapMikMak from '@Feature/OapMikMak';
import OapRegisterNowPopup from '@Feature/OapRegisterNowPopup';
import WishList from '@Feature/WishList';

// 3rd party
import Toasted from '@Feature/OapToastNotification/code/plugin';
import { Tabs, Tab } from 'vue3-tabs-component';

exposeGlobals();

const app = createApp({});
// TODO: To be enabled if removing imports for eventBus makes sense
// app.config.globalProperties.eventBus = eventBus;
app.config.compilerOptions.whitespace = 'preserve';
app.config.performance = true;

const VueTabsPlugin = {
  install(app) {
    app.component('Tabs', Tabs);
    app.component('Tab', Tab);
  },
};

// Bootstrap Vue
app.use(Toasted);
app.use(VueTabsPlugin);

// Register directives
Object.entries({
  ...CommerceConnector.directives,
  ...Foundation.directives,
  ...LoadScriptModule.directives,
  ...NavigationModule.directives,
  ...Search.directives,
  tag,
}).forEach(([name, directive]) => app.directive(name, directive));

// Register component
// TODO: Object.values ???
Object.entries({
  ...AlertModule.components,
  ...ArticleShareItem.components,
  ...BazaarVoiceModule.components,
  ...CarouselModule.components,
  ...ColorDropdown.components,
  ...ColorSelector.components,
  ...CommerceConnector.components,
  ...CookieBanner.components,
  ...FilterModule.components,
  ...Foundation.components,
  ...GenericForm.components,
  ...HaircareDiag.components,
  ...HaircolorDiag.components,
  ...HaircolorTreat.components,
  ...HeaderLogo.components,
  ...Iframepopup.components,
  ...InstagramComponent.components,
  ...likebutton.components,
  ...LiveChat.components,
  ...LoadScriptModule.components,
  ...ModalModule.components,
  ...NavigationModule.components,
  ...NewsletterModule.components,
  ...NotificationModule.components,
  ...OapAccountNavigation.components,
  ...OapBadge.components,
  ...OapBeautyGeniusCard.components,
  ...OapBeautyGeniusWrapper.components,
  ...OapBeautyProfile.components,
  ...OapCard.components,
  ...OapCardPopin.components,
  ...OapCarouselControls.components,
  ...OapChannelAdvisor.components,
  ...OapConcerns.components,
  ...OapContrastSwitcher.components,
  ...OapCountdown.components,
  ...OapDsfAppVtoWrapper.components,
  ...OapDsfAppWrapper.components,
  ...OapEventfulTextInput.components,
  ...OapEventsNavigation.components,
  ...OapFavorite.components,
  ...OapFloatingIsland.components,
  ...OapFlowplayer.components,
  ...OapFlowplayerPopin.components,
  ...OapFocusTrap.components,
  ...OapGeolocationPopin.components,
  ...OapIframe.components,
  ...OapBeforeAfter.components,
  ...OapImmersiveBeforeAfter.components,
  ...OapImmersiveBeforeAfterVideo.components,
  ...OapImmersiveCopyOnVideo.components,
  ...OapImmersiveHotspots.components,
  ...OapImmersiveParallax.components,
  ...OapImmersiveParallaxHero.components,
  ...OapLoader.components,
  ...OapMarquee.components,
  ...OapMakeTheSwitch.components,
  ...OapMarkupRenderingDelay.components,
  ...OapMediaCarousel.components,
  ...OapMediaCover.components,
  ...OapMenShadeMatcher.components,
  ...OapPopin.components,
  ...OapPopup.components,
  ...OapProductIngredients.components,
  ...OapProductVariantSelector.components,
  ...OapPageNavigation.components,
  ...OapPromoBar.components,
  ...OapQuiz.components,
  ...OapRating.components,
  ...OapRoutineBuilder.components,
  ...OapScroller.components,
  ...OapSkindrAppWrapper.components,
  ...OapSlider.components,
  ...OapStories.components,
  ...OapStrikethroughScrolling.components,
  ...OapTabComponent.components,
  ...OapToastNotification.components,
  ...OapUsage.components,
  ...OapVideoImmersive.components,
  ...OneTrustCookieHelper.components,
  ...Overlay.components,
  ...productModule.components,
  ...RealDialog.components,
  ...RevTrax.components,
  ...SearchSuggestions.components,
  ...Sfmc.components,
  ...Slider.components,
  ...VideoPlaceholder.components,
  ...VirtualTryOn.components,
  ...VueModels.components,
  ...ERetailer.components,
  ...OapShadeFinderCarousel.components,
  ...OapTechDetails.components,
  ...OapSustainability.components,
  ...OapPromoCard.components,
  ...FooterNewsletterSignupModal.components,
  ...FloatingSidebar.components,
  ...OapMikMak.components,
  ...OapRegisterNowPopup.components,
  ...WishList.components,
}).forEach(([name, component]) => app.component(name, component));

Object.entries({
  ...Foundation.filters,
}).forEach(([name, filter]) => app.filter(name, filter));

const store = createStore({
  plugins: [...VirtualTryOn.plugins],
  modules: {
    ...FilterModule.modules,
    ...OapBeautyProfile.modules,
    ...OapFavorite.modules,
    ...OapMakeTheSwitch.modules,
    ...OapTabComponent.modules,
    ...productModule.modules,
    ...VirtualTryOn.modules,
    ...AccessibilityStore.modules,
  },
});

app.use(store);

/**
 * Initialize any zone-level customizations.
 * Every zone/country can create their own Vue components and make them available
 * to their code through the use of a Vue.js Plugin
 *
 * @see https://vuejs.org/guide/reusability/plugins.html#writing-a-plugin
 * */
loadZoneCustomizations(app, store);
// Initialize third party loader
init();

// Components that Vue should ignore
// Vue.config.ignoredElements = ['skindr-app', 'dsf-app'];

requireAll(require.context('../Icons/', true, /\.svg$/));

document.addEventListener('lazybeforeunveil', (event) =>
  eventBus.emit('image.lazybeforeunveil', event)
);
window.RefAppMctb && app.use(window.RefAppMctb);
window.RefAppAmp && app.use(window.RefAppAmp);
window.RefAppStoreLocator && app.use(window.RefAppStoreLocator);
window.RefAppFaq && app.use(window.RefAppFaq);
window.RefAppRatingsReviews && app.use(window.RefAppRatingsReviews);
window.RefAppBackToTop && app.use(window.RefAppBackToTop);
window.RefAppUserRedirect && app.use(window.RefAppUserRedirect);

hoistJsonScripts();

SitecoreVueAdapter.initializeVueApp(app.mount.bind(app, '#main-container'));
